/*
 * CopyField: a read-only value with a copy button (installation id, a device code, a command).
 * SecretField: a masked secret (the activation key) with reveal and copy. `onReveal` can fetch the full
 * value the first time it is needed.
 *   <CopyField value={server.installationId} mono />
 *   <SecretField masked={server.licenseKeyMasked} onReveal={() => fetchKey(server.id)} />
 */
import { Check, Copy, Eye, EyeSlash } from '@phosphor-icons/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { useCopy } from '@/lib/useCopy'
import { Spinner } from './Spinner'
import { Tooltip } from './Tooltip'

const box =
  'flex h-11 min-w-0 items-center gap-1 rounded-xl border border-hairline-strong bg-surface-soft ps-3 pe-1 shadow-[0_1px_1px_var(--color-control)]'

function ActionButton({
  label,
  onClick,
  children,
  busy,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
  busy?: boolean
}) {
  return (
    <Tooltip content={label}>
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        disabled={busy}
        className="grid size-9 shrink-0 place-items-center rounded-lg text-muted transition-colors hover:bg-control hover:text-ink touch:size-11 [&_svg]:size-[18px]"
      >
        {busy ? <Spinner /> : children}
      </button>
    </Tooltip>
  )
}

export function CopyField({
  value,
  mono = true,
  className,
  label,
}: {
  value: string
  mono?: boolean
  className?: string
  label?: string
}) {
  const { t } = useTranslation('common')
  const [copied, copy] = useCopy()
  return (
    <div className={cn(box, className)} role="group" aria-label={label}>
      <span
        dir={mono ? 'ltr' : undefined}
        className={cn('min-w-0 flex-1 truncate text-14 text-ink', mono && 'text-start font-mono text-13')}
      >
        {value}
      </span>
      <ActionButton label={copied ? t('actions.copied') : t('actions.copy')} onClick={() => copy(value)}>
        {copied ? <Check className="text-teal-600" aria-hidden="true" /> : <Copy aria-hidden="true" />}
      </ActionButton>
    </div>
  )
}

export function SecretField({
  masked,
  value,
  onReveal,
  className,
  label,
}: {
  masked: string
  /** the full secret if already known */
  value?: string
  /** fetches the full secret on first reveal or copy */
  onReveal?: () => Promise<string>
  className?: string
  label?: string
}) {
  const { t } = useTranslation('common')
  const [copied, copy] = useCopy()
  const [secret, setSecret] = useState<string | undefined>(value)
  const [shown, setShown] = useState(false)
  const [busy, setBusy] = useState(false)

  const ensure = async () => {
    if (secret) return secret
    if (!onReveal) return masked
    setBusy(true)
    try {
      const s = await onReveal()
      setSecret(s)
      return s
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className={cn(box, className)} role="group" aria-label={label}>
      <span dir="ltr" className="min-w-0 flex-1 truncate text-start font-mono text-13 tracking-wide text-ink">
        {shown && secret ? secret : masked}
      </span>
      <ActionButton
        label={shown ? t('ui.secret.hide') : t('ui.secret.reveal')}
        busy={busy && !shown}
        onClick={async () => {
          if (shown) return setShown(false)
          await ensure()
          setShown(true)
        }}
      >
        {shown ? <EyeSlash aria-hidden="true" /> : <Eye aria-hidden="true" />}
      </ActionButton>
      <ActionButton
        label={copied ? t('actions.copied') : t('actions.copy')}
        onClick={async () => copy(await ensure())}
      >
        {copied ? <Check className="text-teal-600" aria-hidden="true" /> : <Copy aria-hidden="true" />}
      </ActionButton>
    </div>
  )
}
